import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";
import {
  SPEC_POLICY_FLAG_CATALOG_BY_ID,
  SPEC_POLICY_FLAG_CATALOG_V3,
  SPEC_POLICY_FLAG_CATALOG_ORDER
} from "@automatry/catalog";
import {
  applySpecPolicyDefaultProfileToFlagSet,
  SPEC_POLICY_FLAG_ORG_DEFAULT_LABEL,
  SPEC_POLICY_FLAG_STANDARD_DEFAULT_LABEL,
  SPEC_POLICY_FLAG_CATALOG_VERSION,
  SPEC_POLICY_FLAGS_SCHEMA_VERSION,
  SpecPolicyFlagSetSchema,
  validateSpecPolicyFlagValueForFamily,
  type SourceRef,
  type SpecPolicyActiveValueVector,
  type SpecPolicyDefaultProfile,
  type SpecPolicyFlag,
  type SpecPolicyDefault,
  type SpecPolicyFlagFamilyId,
  type SpecPolicyFlagScope,
  type SpecPolicyFlagSet,
  type SpecPolicyFlagValue
} from "@automatry/schemas";

export const POLICY_FLAG_DEFAULT_LABEL = SPEC_POLICY_FLAG_STANDARD_DEFAULT_LABEL;
export const POLICY_FLAG_ORG_DEFAULT_LABEL = SPEC_POLICY_FLAG_ORG_DEFAULT_LABEL;
export const applyPolicyDefaultProfileToFlagSet =
  applySpecPolicyDefaultProfileToFlagSet;

const USER_DECISION_WHY = "User decision from Controls policy flags review.";

export function buildDefaultPolicyFlagSetForReview(input: {
  readonly projectId: string;
  readonly now?: string;
  readonly defaultProfile?: SpecPolicyDefaultProfile | null;
}): SpecPolicyFlagSet {
  const now = input.now ?? new Date().toISOString();
  const activeFlags: SpecPolicyFlag[] = SPEC_POLICY_FLAG_CATALOG_V3.map((family) => ({
    id: policyFlagId(input.projectId, family.id, family.defaultValue),
    familyId: family.id,
    domain: family.domain,
    scope: {
      level: "project",
      projectId: input.projectId
    },
    value: family.defaultValue,
    humanName: family.name,
    plainDescription: family.plainMeaning,
    impactSummary: family.downstreamConsumer,
    evidenceRefs: [],
    why: POLICY_FLAG_DEFAULT_LABEL,
    confidence: { score: 0.35, label: "low" },
    provenance: "standard-default",
    status: "active",
    history: [],
    decidedAt: now,
    updatedAt: now
  }));
  const canonicalActiveValueVector = sortActiveValueVector(
    activeFlags.map((flag) => ({
      familyId: flag.familyId,
      scope: flag.scope,
      value: flag.value
    }))
  );

  const flagSet = SpecPolicyFlagSetSchema.parse({
    id: `policy-flag-set-${slug(input.projectId)}`,
    projectId: input.projectId,
    version: 1,
    catalogVersion: SPEC_POLICY_FLAG_CATALOG_VERSION,
    schemaVersion: SPEC_POLICY_FLAGS_SCHEMA_VERSION,
    activeFlags,
    supersededFlags: [],
    canonicalActiveValueVector,
    valueFingerprint: hashUnknown(canonicalActiveValueVector),
    explanationFingerprint: hashUnknown(
      activeFlags.map((flag) => ({
        familyId: flag.familyId,
        provenance: flag.provenance,
        why: flag.why,
        evidenceRefs: flag.evidenceRefs.map((sourceRef) => ({
          sourceId: sourceRef.sourceId,
          locator: sourceRef.locator,
          quote: sourceRef.quote ?? ""
        }))
      }))
    ),
    createdAt: now,
    updatedAt: now
  });

  return applyPolicyDefaultProfileToFlagSet({
    flagSet,
    defaultProfile: input.defaultProfile
  });
}

export function buildPolicyDefault(input: {
  readonly profileId: string;
  readonly familyId: SpecPolicyFlagFamilyId;
  readonly value: SpecPolicyFlagValue;
  readonly enabled: boolean;
}): SpecPolicyDefault {
  return {
    id: `policy-default-${slug(input.profileId)}-${slug(input.familyId)}`,
    familyId: input.familyId,
    scopeTemplate: { level: "project" },
    value: input.value,
    why: POLICY_FLAG_ORG_DEFAULT_LABEL,
    appliesWhen: "tender-silent-only",
    enabled: input.enabled
  };
}

export function updatePolicyFlagUserDecision(input: {
  readonly flagSet: SpecPolicyFlagSet;
  readonly familyId: SpecPolicyFlagFamilyId;
  readonly value: SpecPolicyFlagValue;
  readonly now?: string;
}): SpecPolicyFlagSet {
  const validationErrors = validateSpecPolicyFlagValueForFamily(
    input.familyId,
    input.value
  );

  if (validationErrors.length > 0) {
    throw new Error(validationErrors.join("; "));
  }

  const family = SPEC_POLICY_FLAG_CATALOG_BY_ID[input.familyId];
  const existing = input.flagSet.activeFlags.find(
    (flag) => flag.familyId === input.familyId
  );
  const now = input.now ?? new Date().toISOString();
  const nextFlag: SpecPolicyFlag = {
    id:
      existing?.id ??
      policyFlagId(input.flagSet.projectId, input.familyId, input.value),
    familyId: input.familyId,
    domain: family.domain,
    scope: existing?.scope ?? {
      level: "project",
      projectId: input.flagSet.projectId
    },
    value: input.value,
    humanName: family.name,
    plainDescription: family.plainMeaning,
    impactSummary: family.downstreamConsumer,
    evidenceRefs: [],
    why: USER_DECISION_WHY,
    confidence: { score: 1, label: "high" },
    provenance: "user-decision",
    status: "active",
    priorValue: existing?.value,
    history: existing
      ? [
          ...existing.history,
          {
            flagId: existing.id,
            value: existing.value,
            provenance: existing.provenance,
            why: existing.why,
            confidence: existing.confidence,
            evidenceRefs: existing.evidenceRefs,
            newValueFingerprint: hashUnknown(existing.value),
            flagSetVersion: input.flagSet.version,
            catalogVersion: input.flagSet.catalogVersion,
            schemaVersion: input.flagSet.schemaVersion,
            actor: { type: "user", label: "Controls policy flags review" },
            decidedAt: now
          }
        ]
      : [],
    decidedAt: now,
    updatedAt: now
  };
  const activeFlags = [
    ...input.flagSet.activeFlags.filter((flag) => flag.familyId !== input.familyId),
    nextFlag
  ];

  return rebuildPolicyFlagSet({
    existing: input.flagSet,
    projectId: input.flagSet.projectId,
    activeFlags,
    now,
    version: input.flagSet.version + 1
  });
}

export function mergePolicyFlagSetsForReview(input: {
  readonly existing?: SpecPolicyFlagSet | null | undefined;
  readonly discovered: readonly SpecPolicyFlagSet[];
  readonly defaultProfile?: SpecPolicyDefaultProfile | null | undefined;
  readonly now?: string | undefined;
}): SpecPolicyFlagSet | undefined {
  const seed = input.existing ?? input.discovered[0];

  if (!seed) {
    return undefined;
  }

  const selected = new Map<SpecPolicyFlagFamilyId, SpecPolicyFlag>();

  for (const existingFlag of input.existing?.activeFlags ?? []) {
    selected.set(existingFlag.familyId, existingFlag);
  }

  for (const flagSet of input.discovered) {
    for (const flag of flagSet.activeFlags) {
      const current = selected.get(flag.familyId);

      if (current?.provenance === "user-decision") {
        selected.set(
          current.familyId,
          flag.provenance === "ai-extracted"
            ? {
                ...current,
                fieldReasons: {
                  ...current.fieldReasons,
                  newEvidenceAfterUserDecision:
                    "New source evidence was found after your decision; your reviewed value was kept."
                }
              }
            : current
        );
        continue;
      }

      if (!current || policyFlagMergePriority(flag) >= policyFlagMergePriority(current)) {
        selected.set(flag.familyId, flag);
      }
    }
  }

  const activeFlags =
    selected.size > 0
      ? [...selected.values()]
      : [...(input.existing?.activeFlags ?? [])];

  if (activeFlags.length === 0) {
    return input.existing ?? undefined;
  }

  const merged = rebuildPolicyFlagSet({
    existing: seed,
    projectId: seed.projectId,
    activeFlags,
    now: input.now ?? newestPolicyFlagSetTimestamp([seed, ...input.discovered]),
    version: Math.max(seed.version, input.existing?.version ?? seed.version) + 1
  });

  return applyPolicyDefaultProfileToFlagSet({
    flagSet: merged,
    defaultProfile: input.defaultProfile,
    now: merged.updatedAt
  });
}

export function rewritePolicyFlagSetProjectId(
  flagSet: SpecPolicyFlagSet,
  projectId: string
): SpecPolicyFlagSet {
  if (flagSet.projectId === projectId) {
    return flagSet;
  }

  const previousProjectId = flagSet.projectId;
  const rewriteFlag = (flag: SpecPolicyFlag): SpecPolicyFlag => ({
    ...flag,
    scope: rewriteScope(flag.scope, previousProjectId, projectId),
    evidenceRefs: flag.evidenceRefs.map((sourceRef) =>
      rewriteSourceRef(sourceRef, previousProjectId, projectId)
    ),
    history: flag.history.map((entry) => ({
      ...entry,
      evidenceRefs: entry.evidenceRefs?.map((sourceRef) =>
        rewriteSourceRef(sourceRef, previousProjectId, projectId)
      )
    })),
    updatedAt: flag.updatedAt
  });

  return rebuildPolicyFlagSet({
    existing: {
      ...flagSet,
      projectId,
      activeFlags: flagSet.activeFlags.map(rewriteFlag),
      supersededFlags: flagSet.supersededFlags.map(rewriteFlag)
    },
    projectId,
    activeFlags: flagSet.activeFlags.map(rewriteFlag),
    now: flagSet.updatedAt,
    version: flagSet.version
  });
}

export function policyFlagNeedsUserDecision(value: SpecPolicyFlagValue): boolean {
  if (value.kind === "mode") {
    return value.mode === "needs_user_decision";
  }

  if (value.kind === "structured") {
    return Object.values(value).some((entry) => entry === "needs_user_decision");
  }

  return false;
}

function rebuildPolicyFlagSet(input: {
  readonly existing: SpecPolicyFlagSet;
  readonly projectId: string;
  readonly activeFlags: readonly SpecPolicyFlag[];
  readonly now: string;
  readonly version: number;
}): SpecPolicyFlagSet {
  const activeFlags = sortFlags(input.activeFlags);
  const canonicalActiveValueVector = sortActiveValueVector(
    activeFlags.map((flag) => ({
      familyId: flag.familyId,
      scope: flag.scope,
      value: flag.value
    }))
  );

  return SpecPolicyFlagSetSchema.parse({
    ...input.existing,
    projectId: input.projectId,
    version: input.version,
    catalogVersion: SPEC_POLICY_FLAG_CATALOG_VERSION,
    schemaVersion: SPEC_POLICY_FLAGS_SCHEMA_VERSION,
    activeFlags,
    canonicalActiveValueVector,
    valueFingerprint: hashUnknown(canonicalActiveValueVector),
    explanationFingerprint: hashUnknown(
      activeFlags.map((flag) => ({
        familyId: flag.familyId,
        provenance: flag.provenance,
        why: flag.why,
        evidenceRefs: flag.evidenceRefs.map((sourceRef) => ({
          sourceId: sourceRef.sourceId,
          locator: sourceRef.locator,
          quote: sourceRef.quote ?? ""
        }))
      }))
    ),
    updatedAt: input.now
  });
}

function sortFlags(flags: readonly SpecPolicyFlag[]): SpecPolicyFlag[] {
  const order = catalogOrder();

  return [...flags].sort(
    (left, right) =>
      (order.get(left.familyId) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(right.familyId) ?? Number.MAX_SAFE_INTEGER)
  );
}

function policyFlagMergePriority(flag: SpecPolicyFlag): number {
  if (flag.provenance === "user-decision") {
    return 4;
  }

  if (flag.provenance === "ai-extracted") {
    return 3;
  }

  if (flag.provenance === "provider-conflict") {
    return 2;
  }

  return 1;
}

function newestPolicyFlagSetTimestamp(flagSets: readonly SpecPolicyFlagSet[]): string {
  return (
    flagSets
      .map((flagSet) => flagSet.updatedAt)
      .sort()
      .at(-1) ?? new Date().toISOString()
  );
}

function sortActiveValueVector(
  vector: SpecPolicyActiveValueVector
): SpecPolicyActiveValueVector {
  const order = catalogOrder();

  return [...vector].sort(
    (left, right) =>
      (order.get(left.familyId) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(right.familyId) ?? Number.MAX_SAFE_INTEGER)
  );
}

function catalogOrder(): Map<SpecPolicyFlagFamilyId, number> {
  return new Map(
    SPEC_POLICY_FLAG_CATALOG_ORDER.map((familyId, index) => [familyId, index])
  );
}

function rewriteScope(
  scope: SpecPolicyFlagScope,
  previousProjectId: string,
  projectId: string
): SpecPolicyFlagScope {
  return {
    ...scope,
    projectId,
    ...("apartmentTypeId" in scope && scope.apartmentTypeId
      ? {
          apartmentTypeId: rewriteProjectScopedId(
            scope.apartmentTypeId,
            previousProjectId,
            projectId
          )
        }
      : {}),
    ...("requirementId" in scope
      ? {
          requirementId: rewriteProjectScopedId(
            scope.requirementId,
            previousProjectId,
            projectId
          )
        }
      : {})
  } as SpecPolicyFlagScope;
}

function rewriteSourceRef(
  sourceRef: SourceRef,
  previousProjectId: string,
  projectId: string
): SourceRef {
  return {
    ...sourceRef,
    sourceId: rewriteProjectScopedId(sourceRef.sourceId, previousProjectId, projectId),
    locator: sourceRef.locator.replaceAll(previousProjectId, projectId)
  };
}

function rewriteProjectScopedId(
  value: string,
  previousProjectId: string,
  projectId: string
): string {
  if (value === previousProjectId) {
    return projectId;
  }

  return value.startsWith(`${previousProjectId}-`)
    ? `${projectId}${value.slice(previousProjectId.length)}`
    : value;
}

function policyFlagId(
  projectId: string,
  familyId: SpecPolicyFlagFamilyId,
  value: SpecPolicyFlagValue
): string {
  return `policy-flag-${slug(projectId)}-${slug(familyId)}-${hashUnknown(value).slice(
    7,
    17
  )}`;
}

function hashUnknown(value: unknown): string {
  return `sha256:${bytesToHex(sha256(utf8ToBytes(stableStringify(value))))}`;
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
  }

  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;

    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value);
}

function slug(value: string): string {
  const slugged = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return slugged || "flag";
}
